import { makeAutoObservable, runInAction } from 'mobx';
import type { BibleLocation, VariantAnalysis, VerseAnalysisResponse } from '@skjv/common';
import { fetchVerseAnalysis } from '@/api/verse-analysis';

/** Verse analysis panel display data */
export interface VerseAnalysisDisplay {
  analysis: VariantAnalysis;
  location: BibleLocation;
  isVisible: boolean;
}

export type VerseVerdictState = VerseAnalysisResponse | 'loading';

const locationKey = (loc: BibleLocation): string =>
  `${loc.testamentIndex}-${loc.bookIndex}-${loc.chapterIndex}-${loc.verseIndex}`;

/**
 * Verse analysis panel + verdict state.
 *
 * Deliberately NOT part of UiState.hideAllPanels() — the analysis panel is
 * the Strong's panel's sibling, and the Strong's panel is excluded by design.
 */
export class VerseAnalysisState {
  /** Currently displayed analysis panel */
  analysisDisplay: VerseAnalysisDisplay | null = null;

  /** Verdict cache for the verse menu; missing key = not requested yet */
  private verdicts = new Map<string, VerseVerdictState>();

  constructor() {
    makeAutoObservable(this);
  }

  /** Verdict for a location, 'loading' while in flight, undefined if unrequested */
  verdictFor(location: BibleLocation): VerseVerdictState | undefined {
    return this.verdicts.get(locationKey(location));
  }

  /** Idempotently start fetching the verdict for a location */
  loadVerdict(location: BibleLocation): void {
    const key = locationKey(location);
    if (this.verdicts.has(key)) return;
    this.verdicts.set(key, 'loading');
    void fetchVerseAnalysis(location).then((verdict) => {
      runInAction(() => {
        // fail-silent: network error renders as no analysis section
        this.verdicts.set(key, verdict ?? { verdict: 'none' });
      });
    });
  }

  /** Show the verse analysis panel */
  showVerseAnalysis(analysis: VariantAnalysis, location: BibleLocation): void {
    this.analysisDisplay = { analysis, location, isVisible: true };
  }

  /** Fetch a location's verdict and open the panel if it's a problem */
  openAnalysisAt(location: BibleLocation): void {
    void fetchVerseAnalysis(location).then((verdict) => {
      if (verdict?.verdict !== 'problem') return;
      runInAction(() => this.showVerseAnalysis(verdict.analysis, location));
    });
  }

  /** Hide the panel; object survives so the exit animation can play */
  hideVerseAnalysis(): void {
    if (this.analysisDisplay) {
      this.analysisDisplay.isVisible = false;
    }
  }
}
